import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { Modal } from '../../components/common/Modal';
import { PageHeader } from '../../components/common/PageHeader';
import { BengkungCulaBadge } from '../../components/common/BengkungCulaBadge';
import { MemberSelectCombobox } from '../../components/common/MemberSelectCombobox';
import { getSilatRankFromBengkung } from '../../data/bengkungRanks';
import { UserProfile, Role, Gelanggang, RoleItem, PermissionItem } from '../../types';
import { saveViewScroll, restoreViewScroll, resetScrollToTop, navigateBack } from '../../utils/scrollRestoration';
import {
  getMembersDB,
  saveMemberDB,
  getSystemRolesDB,
  saveSystemRolesDB,
  deleteSystemRoleDB,
  getPermissionsDB,
  savePermissionsDB,
  deletePermissionDB,
  getRolePermissionsMapDB,
  saveRolePermissionsMapDB,
  toggleRolePermissionDB,
  setRolePermissionsForRoleDB,
  resetRolesAndPermissionsDB,
  DEFAULT_AVATAR_URL
} from '../../services/db';
import {
  isSuperAdmin,
  isManagementOrTrainer,
  isJurulatih,
  isPengurusanRole,
  sanitizeMemberId
} from '../../utils/formatters';
import {
  ShieldCheck,
  Users,
  Search,
  Filter,
  CheckCircle2,
  Building,
  ArrowUpDown,
  AlertCircle,
  KeyRound,
  Crown,
  Award,
  BookOpen,
  X,
  Edit3,
  Check,
  Shield,
  Phone,
  Plus,
  Trash2,
  RotateCcw,
  Sliders,
  ToggleLeft,
  ToggleRight,
  ListOrdered,
  Lock,
  Unlock,
  Settings,
  Grid,
  List,
  RefreshCw,
  ArrowLeft,
  Briefcase,
  GraduationCap,
  UserCheck,
  Layers,
  ChevronRight,
  Star
} from 'lucide-react';

/**
 * Helper: Convert role name to a clean, URL-safe slug.
 * e.g. "Timbalan Pengerusi" -> "timbalan-pengerusi", "Ketua & Biro" -> "ketua-dan-biro"
 */
export const toRoleSlug = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, 'dan')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/**
 * Helper: Match role item from URL slug or name
 */
export const findRoleBySlug = (slug: string, roleList: RoleItem[]): RoleItem | undefined => {
  if (!slug) return undefined;
  const cleanSlug = slug.toLowerCase().trim();
  return roleList.find(r =>
    toRoleSlug(r.name) === cleanSlug ||
    r.name.toLowerCase().trim() === cleanSlug ||
    r.id.toLowerCase().trim() === cleanSlug ||
    r.name.toLowerCase().replace(/[^a-z0-9]/g, '') === cleanSlug.replace(/[^a-z0-9]/g, '')
  );
};

export const RolesManagementPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  // Pageview Switch: 'roles' (Roles Directory) vs 'permissions' (Permission Control CRUD) vs 'members' (Member Multi-Roles Directory & Management)
  const [pageView, setPageView] = useState<'roles' | 'permissions' | 'members'>('roles');

  // Members state
  const [members, setMembers] = useState<UserProfile[]>([]);

  // Roles state (CRUD enabled) — loaded LIVE from aaPanel MySQL DB (never from localStorage)
  const [roles, setRoles] = useState<RoleItem[]>([]);

  // Permissions state (CRUD enabled) — loaded LIVE from aaPanel MySQL DB (never from localStorage)
  const [permissions, setPermissions] = useState<PermissionItem[]>([]);

  // Role Permissions Mapping state (Matrix Control) — loaded LIVE from aaPanel MySQL DB
  const [rolePermsMap, setRolePermsMap] = useState<Record<string, string[]>>({});

  // Loading state for initial DB fetch & live sync
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Guard against race conditions when user toggles permissions locally
  const isLocalPermUpdate = useRef(false);
  const togglingKeysRef = useRef<Set<string>>(new Set());

  // Load live roles, permissions, role-perms mapping, and members from MySQL DB
  const loadLiveRolesAndPerms = async () => {
    setIsLoadingRoles(true);
    try {
      const [dbRoles, dbPerms, dbRolePerms, dbMembers] = await Promise.all([
        getSystemRolesDB(true),
        getPermissionsDB(true),
        getRolePermissionsMapDB(true),
        getMembersDB()
      ]);
      setRoles(dbRoles);
      setPermissions(dbPerms);
      setRolePermsMap(dbRolePerms);
      setMembers(dbMembers);

      // Check if current URL pathname contains members or a role slug
      const rawPath = window.location.pathname.replace(/^\//, '').split('?')[0].trim();
      if (rawPath === 'roles/members' || rawPath === 'members/roles/members') {
        setPageView('members');
      } else {
        const match = window.location.pathname.match(/^(?:\/members)?\/roles\/([^/?#]+)/);
        if (match && match[1]) {
          const slug = decodeURIComponent(match[1]);
          if (slug === 'members') {
            setPageView('members');
          } else {
            const matched = findRoleBySlug(slug, dbRoles);
            if (matched) {
              setSelectedRoleForInspector(matched.name);
              setPageView('permissions');
              setPermViewMode('inspector');
            } else if (dbRoles.length > 0) {
              setSelectedRoleForInspector(dbRoles[0].name);
            }
          }
        } else {
          if (dbRoles.length > 0 && !dbRoles.some(r => r.name === selectedRoleForInspector)) {
            setSelectedRoleForInspector(dbRoles[0].name);
          }
        }
      }
    } catch (err) {
      console.warn('Load live roles err:', err);
      showToast('error', 'Pangkalan Data', 'Gagal memuat turun data peranan langsung dari pelayan.');
    } finally {
      setIsLoadingRoles(false);
    }
  };

  useEffect(() => {
    loadLiveRolesAndPerms();

    const handleRolesUpdate = () => {
      getSystemRolesDB(true).then(r => setRoles(r));
    };
    const handlePermsUpdate = () => {
      if (isLocalPermUpdate.current) return;
      getPermissionsDB(false).then(p => setPermissions(p));
      getRolePermissionsMapDB(false).then(m => setRolePermsMap(m));
    };

    window.addEventListener('asmm_roles_updated', handleRolesUpdate);
    window.addEventListener('asmm_permissions_updated', handlePermsUpdate);
    return () => {
      window.removeEventListener('asmm_roles_updated', handleRolesUpdate);
      window.removeEventListener('asmm_permissions_updated', handlePermsUpdate);
    };
  }, []);

  // Listen for browser back/forward navigation (popstate) to keep URL and Inspector in sync
  useEffect(() => {
    const handlePopState = () => {
      const pathname = window.location.pathname;
      const cleanPath = pathname.replace(/^\//, '').split('?')[0].trim();
      if (cleanPath === 'roles/members' || cleanPath === 'members/roles/members') {
        setSelectedMemberForRole(null);
        setPageView('members');
        return;
      }
      const match = pathname.match(/^(?:\/members)?\/roles\/([^/?#]+)/);
      if (match && match[1]) {
        const slug = decodeURIComponent(match[1]);
        if (slug === 'members') {
          setSelectedMemberForRole(null);
          setPageView('members');
          return;
        }
        if (roles.length > 0) {
          const matched = findRoleBySlug(slug, roles);
          if (matched) {
            setSelectedRoleForInspector(matched.name);
            setPageView('permissions');
            setPermViewMode('inspector');
            return;
          }
        }
      }
      if (cleanPath === 'roles' || cleanPath === 'members/roles') {
        setSelectedMemberForRole(null);
        setPageView('roles');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [roles]);

  const refreshMembers = async () => {
    const fresh = await getMembersDB();
    setMembers(fresh);
    window.dispatchEvent(new Event('asmm_db_updated'));
  };

  // Update browser URL to role slug
  const updateRoleUrl = (roleName: string) => {
    const slug = toRoleSlug(roleName);
    const isMembersRoles = window.location.pathname.startsWith('/members/roles');
    const targetPath = isMembersRoles ? `/members/roles/${slug}` : `/roles/${slug}`;
    if (window.location.pathname !== targetPath) {
      window.history.pushState({ role: roleName }, '', targetPath);
    }
  };

  // Handler: Select a role to view its hak akses details & assigned ahli
  const handleSelectRoleForHakAkses = (roleName: string) => {
    setSelectedMemberForRole(null);
    setSelectedRoleForInspector(roleName);
    setPageView('permissions');
    setPermViewMode('inspector');
    updateRoleUrl(roleName);
  };

  // Handler: Switch back to roles list
  const handleSwitchToRolesView = () => {
    setSelectedMemberForRole(null);
    setPageView('roles');
    const isMembersRoles = window.location.pathname.startsWith('/members/roles');
    const targetPath = isMembersRoles ? '/members/roles' : '/roles';
    if (window.location.pathname !== targetPath) {
      window.history.pushState(null, '', targetPath);
    }
  };

  // Handler: Switch to Urus Peranan Ahli (Multi-Roles) page view
  const handleSwitchToMembersView = () => {
    setSelectedMemberForRole(null);
    setPageView('members');
    const isMembersRoles = window.location.pathname.startsWith('/members/roles');
    const targetPath = isMembersRoles ? '/members/roles/members' : '/roles/members';
    if (window.location.pathname !== targetPath) {
      window.history.pushState(null, '', targetPath);
    }
  };

  // --- FILTER & SEARCH STATE ---
  // Pageview 1 (Roles Directory) Filters
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [roleCategoryFilter, setRoleCategoryFilter] = useState('all');
  const [roleViewMode, setRoleViewMode] = useState<'list' | 'grid'>('list');

  // Pageview 2 (Permission Control) Filters
  const [permSearchTerm, setPermSearchTerm] = useState('');
  const [permModuleFilter, setPermModuleFilter] = useState('all');
  const [selectedRoleForInspector, setSelectedRoleForInspector] = useState<string>('Pengerusi');
  const [permViewMode, setPermViewMode] = useState<'matrix' | 'inspector'>('inspector');

  // Pageview 3 (Member Multi-Roles Management) Filters
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [memberRoleFilter, setMemberRoleFilter] = useState('all');
  const [memberGelanggangFilter, setMemberGelanggangFilter] = useState('all');
  const [memberOnlyMultiRoles, setMemberOnlyMultiRoles] = useState(false);

  // --- ASSIGNED MEMBERS FOR SELECTED ROLE (Hak Akses Inspector View) ---
  const [assignedSearchTerm, setAssignedSearchTerm] = useState('');
  const [isQuickAssignModalOpen, setIsQuickAssignModalOpen] = useState(false);
  const [quickCandidateId, setQuickCandidateId] = useState('');
  const [quickCandidateGelanggang, setQuickCandidateGelanggang] = useState<Gelanggang>('Pusaka Warisan');
  const [quickCandidateNotes, setQuickCandidateNotes] = useState('');
  const [isSubmittingQuickAssign, setIsSubmittingQuickAssign] = useState(false);

  // --- MODALS STATE ---
  // Modal: Add / Edit Role
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleItem | null>(null);
  const [roleFormName, setRoleFormName] = useState('');
  const [roleFormCategory, setRoleFormCategory] = useState<RoleItem['category']>('Jawatankuasa & Biro');
  const [roleFormDesc, setRoleFormDesc] = useState('');
  const [roleFormCloneFrom, setRoleFormCloneFrom] = useState<string>('');

  // Modal: Add / Edit Permission
  const [isPermModalOpen, setIsPermModalOpen] = useState(false);
  const [editingPerm, setEditingPerm] = useState<PermissionItem | null>(null);
  const [permFormKey, setPermFormKey] = useState('');
  const [permFormName, setPermFormName] = useState('');
  const [permFormModule, setPermFormModule] = useState('Modul Tambahan');
  const [permFormDesc, setPermFormDesc] = useState('');

  // Modal: Assign Member to Multiple Roles
  const [selectedMemberForRole, setSelectedMemberForRole] = useState<UserProfile | null>(null);
  const [selectedRolesForMember, setSelectedRolesForMember] = useState<Role[]>([]);
  const [primaryRoleForMember, setPrimaryRoleForMember] = useState<Role>('Ahli');
  const [newGelanggang, setNewGelanggang] = useState<Gelanggang>('Pusaka Warisan');
  const [roleNotes, setRoleNotes] = useState<string>('');
  const [isSubmittingRoleChange, setIsSubmittingRoleChange] = useState(false);

  // Active members
  const activeMembers = useMemo(() => {
    return members.filter(m => m.status !== 'Menunggu Pengesahan' && m.status !== 'Ditolak');
  }, [members]);

  // Ahli assigned to selectedRoleForInspector (supports multi-roles)
  const assignedMembersForSelectedRole = useMemo(() => {
    return members.filter(m => {
      const mRoles: Role[] = (m.roles && m.roles.length > 0) ? m.roles : [m.role || 'Ahli'];
      return mRoles.includes(selectedRoleForInspector);
    });
  }, [members, selectedRoleForInspector]);

  // Filtered assigned members by search input
  const filteredAssignedMembers = useMemo(() => {
    if (!assignedSearchTerm.trim()) return assignedMembersForSelectedRole;
    const term = assignedSearchTerm.toLowerCase().trim();
    return assignedMembersForSelectedRole.filter(m =>
      m.name.toLowerCase().includes(term) ||
      (m.icNo && m.icNo.toLowerCase().includes(term)) ||
      (m.email && m.email.toLowerCase().includes(term)) ||
      (m.phone && m.phone.toLowerCase().includes(term)) ||
      (m.gelanggang && m.gelanggang.toLowerCase().includes(term)) ||
      m.id.toLowerCase().includes(term)
    );
  }, [assignedMembersForSelectedRole, assignedSearchTerm]);

  // Available candidate members who don't already have this role
  const candidateMembers = useMemo(() => {
    return activeMembers.filter(m => {
      const mRoles: Role[] = (m.roles && m.roles.length > 0) ? m.roles : [m.role || 'Ahli'];
      return !mRoles.includes(selectedRoleForInspector);
    });
  }, [activeMembers, selectedRoleForInspector]);

  const handleOpenQuickAssign = () => {
    if (candidateMembers.length > 0) {
      setQuickCandidateId(candidateMembers[0].id);
      setQuickCandidateGelanggang(candidateMembers[0].gelanggang || 'Pusaka Warisan');
    } else {
      setQuickCandidateId('');
      setQuickCandidateGelanggang('Pusaka Warisan');
    }
    setQuickCandidateNotes('');
    setIsQuickAssignModalOpen(true);
  };

  const handleSaveQuickAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    const candidate = members.find(m => m.id === quickCandidateId);
    if (!candidate) {
      showToast('error', 'Pilih Ahli', 'Sila pilih ahli untuk dilantik.');
      return;
    }

    setIsSubmittingQuickAssign(true);
    try {
      const currentRoles: Role[] = (candidate.roles && candidate.roles.length > 0) ? candidate.roles : [candidate.role || 'Ahli'];
      // Appoint new role alongside existing roles (replace 'Ahli' if candidate was previously only a regular Ahli)
      const updatedRoles: Role[] = Array.from(new Set([
        ...currentRoles.filter(r => r !== 'Ahli' || selectedRoleForInspector === 'Ahli'),
        selectedRoleForInspector as Role
      ]));
      const updated: UserProfile = {
        ...candidate,
        role: candidate.role && candidate.role !== 'Ahli' ? candidate.role : (selectedRoleForInspector as Role),
        roles: updatedRoles,
        gelanggang: quickCandidateGelanggang,
      };

      await saveMemberDB(updated);
      await refreshMembers();

      showToast('success', 'Ahli Dilantik', `${candidate.name} kini dilantik memegang peranan ${selectedRoleForInspector}.`);
      setIsQuickAssignModalOpen(false);
    } catch {
      showToast('error', 'Ralat', 'Gagal melantik ahli ke peranan ini.');
    } finally {
      setIsSubmittingQuickAssign(false);
    }
  };

  // Member count per role (accounts for members holding multi-roles)
  const roleMemberCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    activeMembers.forEach(m => {
      const mRoles: Role[] = (m.roles && m.roles.length > 0) ? m.roles : [m.role || 'Ahli'];
      mRoles.forEach(r => {
        counts[r] = (counts[r] || 0) + 1;
      });
    });
    return counts;
  }, [activeMembers]);

  // Filtered members for Pageview 3 (Multi-Roles Directory)
  const filteredMembersForMultiRoles = useMemo(() => {
    return activeMembers.filter(m => {
      const mRoles: Role[] = (m.roles && m.roles.length > 0) ? m.roles : [m.role || 'Ahli'];

      // Only multi-roles filter
      if (memberOnlyMultiRoles && mRoles.length <= 1) {
        return false;
      }

      // Gelanggang filter
      if (memberGelanggangFilter !== 'all' && m.gelanggang !== memberGelanggangFilter) {
        return false;
      }

      // Role filter
      if (memberRoleFilter !== 'all' && !mRoles.includes(memberRoleFilter as Role)) {
        return false;
      }

      // Search query
      if (memberSearchTerm.trim()) {
        const term = memberSearchTerm.toLowerCase().trim();
        const matchName = m.name?.toLowerCase().includes(term);
        const matchIc = m.icNo?.toLowerCase().includes(term);
        const matchId = m.id?.toLowerCase().includes(term);
        const matchEmail = m.email?.toLowerCase().includes(term);
        const matchPhone = m.phone?.toLowerCase().includes(term);
        const matchGelanggang = m.gelanggang?.toLowerCase().includes(term);
        const matchRole = mRoles.some(r => r.toLowerCase().includes(term));
        if (!matchName && !matchIc && !matchId && !matchEmail && !matchPhone && !matchGelanggang && !matchRole) {
          return false;
        }
      }

      return true;
    });
  }, [activeMembers, memberSearchTerm, memberRoleFilter, memberGelanggangFilter, memberOnlyMultiRoles]);

  // KPI statistics for Pageview 3 (Multi-Roles Management)
  const memberKpis = useMemo(() => {
    let multiRoleCount = 0;
    let leadershipCount = 0;
    let trainerCount = 0;

    activeMembers.forEach(m => {
      const mRoles: Role[] = (m.roles && m.roles.length > 0) ? m.roles : [m.role || 'Ahli'];
      if (mRoles.length > 1) {
        multiRoleCount++;
      }
      if (isSuperAdmin(m) || mRoles.some(r => isPengurusanRole(r) || r === 'Pengerusi' || r === 'Timbalan Pengerusi' || r === 'Setiausaha' || r === 'Bendahari' || r === 'Ketua Gelanggang')) {
        leadershipCount++;
      }
      if (isJurulatih(m)) {
        trainerCount++;
      }
    });

    return {
      total: activeMembers.length,
      multiRole: multiRoleCount,
      leadership: leadershipCount,
      trainer: trainerCount,
    };
  }, [activeMembers]);

  // Unique modules list
  const uniqueModules = useMemo(() => {
    const set = new Set<string>();
    permissions.forEach(p => set.add(p.module));
    return Array.from(set);
  }, [permissions]);

  // Filtered Roles list
  const filteredRoles = useMemo(() => {
    return roles.filter(r => {
      const matchSearch = roleSearchTerm === '' ||
        r.name.toLowerCase().includes(roleSearchTerm.toLowerCase()) ||
        r.description.toLowerCase().includes(roleSearchTerm.toLowerCase());
      const matchCat = roleCategoryFilter === 'all' || r.category === roleCategoryFilter;
      return matchSearch && matchCat;
    });
  }, [roles, roleSearchTerm, roleCategoryFilter]);

  // Filtered Permissions list
  const filteredPermissions = useMemo(() => {
    return permissions.filter(p => {
      const matchSearch = permSearchTerm === '' ||
        p.name.toLowerCase().includes(permSearchTerm.toLowerCase()) ||
        p.key.toLowerCase().includes(permSearchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(permSearchTerm.toLowerCase());
      const matchModule = permModuleFilter === 'all' || p.module === permModuleFilter;
      return matchSearch && matchModule;
    });
  }, [permissions, permSearchTerm, permModuleFilter]);

  // Category badge color
  const getCategoryBadgeClass = (category: RoleItem['category']) => {
    switch (category) {
      case 'Majlis Tertinggi':
        return 'bg-amber-100 text-amber-900 border-amber-300 font-extrabold';
      case 'Pengurusan Gelanggang':
        return 'bg-purple-100 text-purple-900 border-purple-300 font-extrabold';
      case 'Jawatankuasa & Biro':
        return 'bg-emerald-100 text-emerald-900 border-emerald-300 font-extrabold';
      case 'Kejurulatihan':
        return 'bg-red-100 text-red-900 border-red-300 font-extrabold';
      case 'Keahlian Am':
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200 font-bold';
    }
  };

  // --- CRUD: ROLES ---
  const handleOpenAddRole = () => {
    setEditingRole(null);
    setRoleFormName('');
    setRoleFormCategory('Jawatankuasa & Biro');
    setRoleFormDesc('');
    setRoleFormCloneFrom('');
    setIsRoleModalOpen(true);
  };

  const handleOpenEditRole = (role: RoleItem) => {
    setEditingRole(role);
    setRoleFormName(role.name);
    setRoleFormCategory(role.category);
    setRoleFormDesc(role.description);
    setRoleFormCloneFrom('');
    setIsRoleModalOpen(true);
  };

  const handleSaveRole = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanName = roleFormName.trim();
    if (!cleanName) {
      showToast('error', 'Nama Wajib', 'Sila masukkan nama peranan yang sah.');
      return;
    }

    setIsSaving(true);
    try {
      if (editingRole) {
        // Edit existing role
        const updatedRoles = roles.map(r => r.id === editingRole.id ? {
          ...r,
          name: cleanName,
          category: roleFormCategory,
          description: roleFormDesc.trim() || r.description
        } : r);

        // Update mapping key if name changed
        let updatedMap = { ...rolePermsMap };
        if (editingRole.name !== cleanName) {
          updatedMap[cleanName] = updatedMap[editingRole.name] || [];
          delete updatedMap[editingRole.name];
        }

        // Persist LIVE to aaPanel MySQL DB
        const success = await saveSystemRolesDB(updatedRoles);
        if (editingRole.name !== cleanName) {
          await saveRolePermissionsMapDB(updatedMap);
        }

        if (success) {
          setRoles(updatedRoles);
          setRolePermsMap(updatedMap);
          showToast('success', 'Peranan Dikemaskini', `Peranan ${cleanName} berjaya dikemaskini dalam pangkalan data.`);
          setIsRoleModalOpen(false);
        } else {
          showToast('error', 'Ralat Simpan', 'Pangkalan data gagal mengemaskini peranan.');
        }
      } else {
        // Add new role
        const roleId = cleanName.toLowerCase().replace(/[^a-z0-9]/g, '-');
        if (roles.some(r => r.name.toLowerCase() === cleanName.toLowerCase())) {
          showToast('error', 'Peranan Telah Wujud', `Peranan ${cleanName} sudah didaftarkan.`);
          return;
        }

        const newRoleObj: RoleItem = {
          id: roleId,
          name: cleanName,
          category: roleFormCategory,
          description: roleFormDesc.trim() || 'Peranan tersuai ASMM',
          isSystem: false,
          sortOrder: roles.length + 1
        };

        const clonedPerms = roleFormCloneFrom && rolePermsMap[roleFormCloneFrom]
          ? [...rolePermsMap[roleFormCloneFrom]]
          : [];

        const updatedRoles = [...roles, newRoleObj];
        const updatedMap = { ...rolePermsMap, [cleanName]: clonedPerms };

        // Persist LIVE to aaPanel MySQL DB
        const success = await saveSystemRolesDB(updatedRoles);
        if (clonedPerms.length > 0) {
          await saveRolePermissionsMapDB(updatedMap);
        }

        if (success) {
          setRoles(updatedRoles);
          setRolePermsMap(updatedMap);
          showToast('success', 'Peranan Ditambah', `Peranan baharu ${cleanName} berjaya didaftarkan ke pangkalan data.`);
          setIsRoleModalOpen(false);
        } else {
          showToast('error', 'Ralat Simpan', 'Pangkalan data gagal menyimpan peranan baharu.');
        }
      }
    } catch {
      showToast('error', 'Ralat', 'Gagal memproses peranan.');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete confirmation modal state
  const [confirmDeleteRole, setConfirmDeleteRole] = useState<RoleItem | null>(null);
  const [confirmDeletePerm, setConfirmDeletePerm] = useState<PermissionItem | null>(null);
  const [confirmResetAll, setConfirmResetAll] = useState(false);

  const handleDeleteRole = (role: RoleItem) => {
    if (role.isSystem) {
      showToast('error', 'Peranan Sistem', 'Peranan asas pertubuhan tidak boleh dipadam.');
      return;
    }

    const assignedCount = roleMemberCounts[role.name] || 0;
    if (assignedCount > 0) {
      showToast('error', 'Masih Ada Ahli', `Terdapat ${assignedCount} ahli memegang peranan ini. Sila tukar peranan mereka dahulu.`);
      return;
    }

    setConfirmDeleteRole(role);
  };

  const executeDeleteRole = async () => {
    if (!confirmDeleteRole) return;
    const role = confirmDeleteRole;
    setIsSaving(true);
    try {
      const updatedRoles = roles.filter(r => r.id !== role.id);
      const updatedMap = { ...rolePermsMap };
      delete updatedMap[role.name];

      // Persist LIVE to aaPanel MySQL DB
      const success = await deleteSystemRoleDB(role.id, role.name);
      if (success) {
        setRoles(updatedRoles);
        setRolePermsMap(updatedMap);
        if (selectedRoleForInspector === role.name && updatedRoles.length > 0) {
          setSelectedRoleForInspector(updatedRoles[0].name);
        }
        showToast('info', 'Peranan Dipadam', `Peranan ${role.name} telah dipadam dari pangkalan data.`);
      } else {
        showToast('error', 'Ralat Padam', 'Gagal memadam peranan dari pangkalan data.');
      }
    } catch {
      showToast('error', 'Ralat', 'Gagal memadam peranan.');
    } finally {
      setIsSaving(false);
      setConfirmDeleteRole(null);
    }
  };

  // --- CRUD: PERMISSIONS ---
  const handleOpenAddPerm = () => {
    setEditingPerm(null);
    setPermFormKey('');
    setPermFormName('');
    setPermFormModule('Tadbir Urus');
    setPermFormDesc('');
    setIsPermModalOpen(true);
  };

  const handleOpenEditPerm = (perm: PermissionItem) => {
    setEditingPerm(perm);
    setPermFormKey(perm.key);
    setPermFormName(perm.name);
    setPermFormModule(perm.module);
    setPermFormDesc(perm.description);
    setIsPermModalOpen(true);
  };

  const handleSavePerm = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanKey = permFormKey.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
    const cleanName = permFormName.trim();
    if (!cleanKey || !cleanName) {
      showToast('error', 'Maklumat Tidak Lengkap', 'Sila isi kod dan nama kebenaran.');
      return;
    }

    setIsSaving(true);
    try {
      if (editingPerm) {
        // Edit existing permission
        const updatedPerms = permissions.map(p => p.key === editingPerm.key ? {
          ...p,
          name: cleanName,
          module: permFormModule,
          description: permFormDesc.trim() || p.description
        } : p);

        // Persist LIVE
        const success = await savePermissionsDB(updatedPerms);
        if (success) {
          setPermissions(updatedPerms);
          showToast('success', 'Hak Akses Dikemaskini', `Hak akses ${cleanName} dikemaskini dalam pangkalan data.`);
          setIsPermModalOpen(false);
        } else {
          showToast('error', 'Ralat Simpan', 'Pangkalan data gagal mengemaskini hak akses.');
        }
      } else {
        // Add new permission
        if (permissions.some(p => p.key === cleanKey)) {
          showToast('error', 'Kod Wujud', `Kod hak akses "${cleanKey}" sudah wujud.`);
          return;
        }

        const newPermObj: PermissionItem = {
          key: cleanKey,
          name: cleanName,
          module: permFormModule,
          description: permFormDesc.trim() || 'Hak akses modul tersuai',
          isSystem: false,
          sortOrder: permissions.length + 1
        };

        const updatedPerms = [...permissions, newPermObj];
        const updatedMap = {
          ...rolePermsMap,
          'Pengerusi': [...(rolePermsMap['Pengerusi'] || []), cleanKey],
          'Timbalan Pengerusi': [...(rolePermsMap['Timbalan Pengerusi'] || []), cleanKey],
        };

        // Persist LIVE
        const success = await savePermissionsDB(updatedPerms);
        await saveRolePermissionsMapDB(updatedMap);

        if (success) {
          setPermissions(updatedPerms);
          setRolePermsMap(updatedMap);
          showToast('success', 'Hak Akses Ditambah', `Hak akses ${cleanName} berjaya didaftarkan ke pangkalan data.`);
          setIsPermModalOpen(false);
        } else {
          showToast('error', 'Ralat Simpan', 'Pangkalan data gagal mendaftar hak akses.');
        }
      }
    } catch {
      showToast('error', 'Ralat', 'Gagal memproses hak akses.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePerm = (perm: PermissionItem) => {
    if (perm.isSystem) {
      showToast('error', 'Hak Akses Sistem', 'Hak akses asas portal tidak boleh dipadam.');
      return;
    }

    setConfirmDeletePerm(perm);
  };

  const executeDeletePerm = async () => {
    if (!confirmDeletePerm) return;
    const perm = confirmDeletePerm;
    setIsSaving(true);
    try {
      const updatedPerms = permissions.filter(p => p.key !== perm.key);
      const updatedMap: Record<string, string[]> = {};
      Object.keys(rolePermsMap).forEach(roleName => {
        updatedMap[roleName] = (rolePermsMap[roleName] || []).filter(k => k !== perm.key);
      });

      // Persist LIVE to aaPanel MySQL DB
      const success = await deletePermissionDB(perm.key);
      if (success) {
        setPermissions(updatedPerms);
        setRolePermsMap(updatedMap);
        showToast('info', 'Hak Akses Dipadam', `Hak akses ${perm.name} telah dipadam dari pangkalan data.`);
      } else {
        showToast('error', 'Ralat Padam', 'Gagal memadam hak akses dari pangkalan data.');
      }
    } catch {
      showToast('error', 'Ralat', 'Gagal memadam hak akses.');
    } finally {
      setIsSaving(false);
      setConfirmDeletePerm(null);
    }
  };

  // --- PERMISSION TOGGLE IN MATRIX & INSPECTOR (LIVE ATOMIC UPDATE) ---
  const handleTogglePermission = async (roleName: string, permKey: string) => {
    const toggleId = `${roleName}:${permKey}`;
    if (togglingKeysRef.current.has(toggleId)) return;
    togglingKeysRef.current.add(toggleId);
    isLocalPermUpdate.current = true;

    const currentList = rolePermsMap[roleName] || [];
    const hasIt = currentList.includes(permKey);
    const nextList = hasIt
      ? currentList.filter(k => k !== permKey)
      : [...currentList, permKey];

    // Optimistic UI state - instant 0ms checkmark update
    setRolePermsMap(prev => ({ ...prev, [roleName]: nextList }));

    try {
      // Persist LIVE atomic update to aaPanel MySQL DB
      const ok = await toggleRolePermissionDB(roleName, permKey, !hasIt);
      if (!ok) {
        // Revert only if database rejected
        setRolePermsMap(prev => ({ ...prev, [roleName]: currentList }));
        showToast('error', 'Ralat Kemaskini', 'Gagal mengemaskini hak akses ke pangkalan data.');
      }
    } catch {
      setRolePermsMap(prev => ({ ...prev, [roleName]: currentList }));
      showToast('error', 'Ralat', 'Gagal mengemaskini hak akses.');
    } finally {
      togglingKeysRef.current.delete(toggleId);
      setTimeout(() => {
        isLocalPermUpdate.current = false;
      }, 600);
    }
  };

  const handleGrantAllForRole = async (roleName: string) => {
    const allKeys = permissions.map(p => p.key);
    const prevKeys = rolePermsMap[roleName] || [];
    setRolePermsMap(prev => ({ ...prev, [roleName]: allKeys }));

    // Persist LIVE to aaPanel MySQL DB
    const ok = await setRolePermissionsForRoleDB(roleName, allKeys);
    if (ok) {
      showToast('info', 'Pemberian Penuh', `Semua hak akses diberikan kepada peranan ${roleName}.`);
    } else {
      setRolePermsMap(prev => ({ ...prev, [roleName]: prevKeys }));
      showToast('error', 'Ralat', 'Gagal menyimpan hak akses penuh.');
    }
  };

  const handleRevokeAllForRole = async (roleName: string) => {
    const prevKeys = rolePermsMap[roleName] || [];
    setRolePermsMap(prev => ({ ...prev, [roleName]: [] }));

    // Persist LIVE to aaPanel MySQL DB
    const ok = await setRolePermissionsForRoleDB(roleName, []);
    if (ok) {
      showToast('info', 'Dikosongkan', `Semua hak akses dipadamkan bagi peranan ${roleName}.`);
    } else {
      setRolePermsMap(prev => ({ ...prev, [roleName]: prevKeys }));
      showToast('error', 'Ralat', 'Gagal mengosongkan hak akses.');
    }
  };

  const handleResetToDefaultPermissions = () => {
    setConfirmResetAll(true);
  };

  const executeResetAll = async () => {
    setConfirmResetAll(false);
    setIsSaving(true);
    try {
      const result = await resetRolesAndPermissionsDB();
      setRoles(result.roles);
      setPermissions(result.permissions);
      setRolePermsMap(result.rolePermsMap);
      showToast('success', 'Tetapan Direset', 'Semua peranan dan hak akses kini mengikut tetapan lalai rasmi ASMM.');
    } catch {
      showToast('error', 'Ralat', 'Gagal menetapkan semula tetapan.');
    } finally {
      setIsSaving(false);
    }
  };

  // --- MEMBER MULTI-ROLES MANAGEMENT (ZERO MODAL FULL-PAGE VIEW) ---
  const handleOpenAssignModal = (member: UserProfile) => {
    setSelectedMemberForRole(member);
    const mRoles: Role[] = (member.roles && member.roles.length > 0) ? [...member.roles] : [member.role || 'Ahli'];
    setSelectedRolesForMember(mRoles);
    setPrimaryRoleForMember(member.role || mRoles[0] || 'Ahli');
    setNewGelanggang(member.gelanggang || 'Pusaka Warisan');
    setRoleNotes('');
    setPageView('members');
  };
  const handleOpenAssignPageView = handleOpenAssignModal;

  const handleToggleRoleInModal = (roleName: Role) => {
    setSelectedRolesForMember(prev => {
      let next: Role[];
      if (prev.includes(roleName)) {
        next = prev.filter(r => r !== roleName);
        if (next.length === 0) {
          next = ['Ahli'];
        }
      } else {
        if (prev.length === 1 && prev[0] === 'Ahli' && roleName !== 'Ahli') {
          next = [roleName];
        } else {
          next = [...prev.filter(r => r !== 'Ahli' || roleName === 'Ahli'), roleName];
        }
      }
      setPrimaryRoleForMember(curr => (next.includes(curr) ? curr : next[0] || 'Ahli'));
      return next;
    });
  };

  const handleSaveMemberRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMemberForRole) return;

    if (selectedRolesForMember.length === 0) {
      showToast('error', 'Pilih Peranan', 'Sila pilih sekurang-kurangnya satu peranan.');
      return;
    }

    setIsSubmittingRoleChange(true);
    try {
      const finalRoles = Array.from(new Set(selectedRolesForMember));
      const finalPrimary = (primaryRoleForMember && finalRoles.includes(primaryRoleForMember))
        ? primaryRoleForMember
        : finalRoles[0];

      const updated: UserProfile = {
        ...selectedMemberForRole,
        role: finalPrimary,
        roles: finalRoles,
        gelanggang: newGelanggang,
      };

      await saveMemberDB(updated);
      await refreshMembers();

      showToast('success', 'Peranan Dikemaskini', `Peranan bagi ${selectedMemberForRole.name} berjaya dikemaskini (${finalRoles.join(', ')}).`);
      setSelectedMemberForRole(null);
    } catch {
      showToast('error', 'Ralat', 'Gagal mengemaskini peranan ahli.');
    } finally {
      setIsSubmittingRoleChange(false);
    }
  };

  const handleRemoveRoleFromMember = async (member: UserProfile, roleToRemove: string) => {
    const currentRoles: Role[] = (member.roles && member.roles.length > 0) ? member.roles : [member.role || 'Ahli'];
    const remainingRoles = currentRoles.filter(r => r !== roleToRemove);
    const finalRoles: Role[] = remainingRoles.length > 0 ? remainingRoles : ['Ahli'];
    const finalPrimary = finalRoles.includes(member.role) ? member.role : finalRoles[0];

    try {
      const updated: UserProfile = {
        ...member,
        role: finalPrimary,
        roles: finalRoles
      };
      await saveMemberDB(updated);
      await refreshMembers();
      showToast('info', 'Peranan Digugurkan', `Peranan "${roleToRemove}" telah digugurkan daripada ${member.name}.`);
    } catch {
      showToast('error', 'Ralat', 'Gagal menggugurkan peranan daripada ahli.');
    }
  };

  if (!isSuperAdmin(currentUser)) {
    return (
      <div className="w-full min-h-[60vh] flex flex-col items-center justify-center p-8 bg-white rounded-3xl border border-slate-200 text-center space-y-4">
        <div className="w-16 h-16 bg-red-50 text-asmm-red rounded-full flex items-center justify-center">
          <ShieldCheck className="w-8 h-8 text-asmm-red" />
        </div>
        <div className="space-y-1">
          <h2 className="text-lg font-bold text-slate-900">Akses Terhad</h2>
          <p className="text-xs text-slate-500 max-w-md">
            Halaman Roles Management hanya boleh diakses dan diuruskan oleh Pentadbir Utama (Superadmin) ASMM sahaja.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigateBack('/members')}
          className="px-5 py-2.5 bg-slate-900 text-white text-xs font-bold rounded-xl hover:bg-slate-800 transition-all cursor-pointer border-none shadow-sm"
        >
          Kembali ke Direktori Ahli
        </button>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 min-h-[75vh] animate-fade-in">
      {/* PAGE HEADER */}
      <PageHeader
        badgeText="Roles & Permission Control"
        badgeIcon={ShieldCheck}
        title="Roles Management"
        titleBadge={roles.length}
      />

      {isLoadingRoles ? (
        <div className="w-full bg-white rounded-3xl border border-slate-200/90 shadow-2xs p-12 flex flex-col items-center justify-center space-y-4">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-asmm-red rounded-full animate-spin" />
          <div className="text-center space-y-1">
            <h3 className="text-sm font-black text-slate-800">Menyambung ke Pangkalan Data Langsung...</h3>
            <p className="text-xs text-slate-500">Memuat turun senarai peranan dan kebenaran hak akses dari aaPanel MySQL.</p>
          </div>
        </div>
      ) : (
        <>
          {/* TOP PAGEVIEW SWITCHER (3 DISTINCT PAGEVIEWS) */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white p-2 rounded-2xl border border-slate-200/90 shadow-2xs">
            <div className="flex items-center gap-2 flex-wrap">
              {/* PAGEVIEW 1 BUTTON */}
              <button
                type="button"
                onClick={handleSwitchToRolesView}
                className={`flex-1 sm:flex-initial px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer border-none ${pageView === 'roles'
                    ? 'bg-asmm-red text-white shadow-xs font-extrabold'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
              >
                <Users className="w-4 h-4" />
                <span>Senarai Peranan</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${pageView === 'roles' ? 'bg-red-900 text-white' : 'bg-slate-100 text-slate-800'
                  }`}>
                  {roles.length}
                </span>
              </button>

              {/* PAGEVIEW 2 BUTTON */}
              <button
                type="button"
                onClick={() => handleSelectRoleForHakAkses(selectedRoleForInspector)}
                className={`flex-1 sm:flex-initial px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer border-none ${pageView === 'permissions'
                    ? 'bg-asmm-red text-white shadow-xs font-extrabold'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
              >
                <KeyRound className="w-4 h-4" />
                <span>Hak Akses</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${pageView === 'permissions' ? 'bg-red-900 text-white' : 'bg-slate-100 text-slate-800'
                  }`}>
                  {permissions.length}
                </span>
              </button>

              {/* PAGEVIEW 3 BUTTON: URUS PERANAN AHLI (MULTI-ROLES) */}
              <button
                type="button"
                onClick={handleSwitchToMembersView}
                className={`flex-1 sm:flex-initial px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer border-none ${pageView === 'members'
                    ? 'bg-asmm-red text-white shadow-xs font-extrabold'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
              >
                <ShieldCheck className="w-4 h-4" />
                <span>Urus Peranan Ahli</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${pageView === 'members' ? 'bg-red-900 text-white' : 'bg-slate-100 text-slate-800'
                  }`}>
                  {activeMembers.length}
                </span>
              </button>
            </div>

            {/* TOP QUICK ACTION BASED ON CURRENT PAGEVIEW */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={loadLiveRolesAndPerms}
                disabled={isLoadingRoles || isSaving}
                className="px-3.5 py-2 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer border border-slate-200 shadow-2xs disabled:opacity-50 active:scale-95"
                title="Muat semula data peranan secara langsung dari aaPanel MySQL"
              >
                <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${isLoadingRoles ? 'animate-spin' : ''}`} />
                <span>Segarkan</span>
              </button>

              {pageView === 'roles' ? (
                <button
                  type="button"
                  onClick={handleOpenAddRole}
                  className="w-full sm:w-auto px-4 py-2 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-extrabold rounded-xl shadow-xs transition-all flex items-center justify-center gap-1.5 cursor-pointer border-none active:scale-95"
                >
                  <Plus className="w-4 h-4" />
                  <span>Tambah Peranan</span>
                </button>
              ) : pageView === 'permissions' ? (
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={handleResetToDefaultPermissions}
                    className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer border border-slate-200"
                    title="Set semula semua hak akses ke tetapan lalai sistem"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span className="hidden md:inline">Reset Lalai</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenAddPerm}
                    className="px-4 py-2 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-extrabold rounded-xl shadow-xs transition-all flex items-center justify-center gap-1.5 cursor-pointer border-none active:scale-95"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Tambah Akses</span>
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  {selectedMemberForRole ? (
                    <button
                      type="button"
                      onClick={() => setSelectedMemberForRole(null)}
                      className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer border border-slate-200 active:scale-95"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" />
                      <span>Kembali ke Senarai</span>
                    </button>
                  ) : (
                    <span className="text-xs font-bold text-slate-600 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200 hidden sm:inline-flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-asmm-red" />
                      <span><strong>{memberKpis.multiRole}</strong> Ahli Multi-Roles</span>
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ========================================================================= */}
          {/* PAGEVIEW 1: SENARAI PERANAN (ROLES DIRECTORY & ROLES CRUD)                */}
          {/* ========================================================================= */}
          {pageView === 'roles' && (
            <div className="space-y-6 animate-fade-in">
              {/* Top KPI Metrics for Roles */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                <div className="bg-white p-4 rounded-3xl border border-slate-200/90 shadow-2xs space-y-1">
                  <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Jumlah Peranan</span>
                  <div className="text-2xl font-black text-slate-900">{roles.length}</div>
                  <p className="text-[11px] text-slate-500">Peranan dalam sistem ASMM</p>
                </div>
                <div className="bg-white p-4 rounded-3xl border border-slate-200/90 shadow-2xs space-y-1">
                  <span className="text-[11px] font-bold text-amber-700 uppercase tracking-wider">Majlis Tertinggi</span>
                  <div className="text-2xl font-black text-amber-900">
                    {roles.filter(r => r.category === 'Majlis Tertinggi').length}
                  </div>
                  <p className="text-[11px] text-slate-500">Pengerusi, Timbalan, Setiausaha, Bendahari</p>
                </div>
                <div className="bg-white p-4 rounded-3xl border border-slate-200/90 shadow-2xs space-y-1">
                  <span className="text-[11px] font-bold text-purple-700 uppercase tracking-wider">Gelanggang & Biro</span>
                  <div className="text-2xl font-black text-purple-900">
                    {roles.filter(r => r.category === 'Pengurusan Gelanggang' || r.category === 'Jawatankuasa & Biro').length}
                  </div>
                  <p className="text-[11px] text-slate-500">Pentadbiran pusat & biro kerja</p>
                </div>
                <div className="bg-white p-4 rounded-3xl border border-slate-200/90 shadow-2xs space-y-1">
                  <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider">Pemegang Jawatan</span>
                  <div className="text-2xl font-black text-emerald-900">
                    {activeMembers.filter(m => {
                      const mRoles = (m.roles && m.roles.length > 0) ? m.roles : [m.role || 'Ahli'];
                      return mRoles.some(r => r !== 'Ahli' && r !== 'Waris');
                    }).length}
                  </div>
                  <p className="text-[11px] text-slate-500">Pesilat memegang amanah</p>
                </div>
              </div>

              {/* Filter Bar */}
              <div className="bg-white p-4 rounded-3xl border border-slate-200 shadow-2xs flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3.5 top-2.5 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={roleSearchTerm}
                    onChange={(e) => setRoleSearchTerm(e.target.value)}
                    placeholder="Cari nama peranan atau deskripsi tugas..."
                    className="w-full pl-10 pr-9 py-2 rounded-2xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                  />
                  {roleSearchTerm && (
                    <button
                      type="button"
                      onClick={() => setRoleSearchTerm('')}
                      className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto shrink-0">
                  <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-2xl border border-slate-200 text-xs">
                    <Filter className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="text-slate-500 font-bold hidden sm:inline">Kategori:</span>
                    <select
                      value={roleCategoryFilter}
                      onChange={(e) => setRoleCategoryFilter(e.target.value)}
                      className="bg-transparent text-xs font-bold text-slate-800 focus:outline-none cursor-pointer border-none"
                    >
                      <option value="all">Semua Kategori</option>
                      <option value="Majlis Tertinggi">Majlis Tertinggi</option>
                      <option value="Pengurusan Gelanggang">Pengurusan Gelanggang</option>
                      <option value="Jawatankuasa & Biro">Jawatankuasa & Biro</option>
                      <option value="Kejurulatihan">Kejurulatihan</option>
                      <option value="Keahlian Am">Keahlian Am</option>
                    </select>
                  </div>

                  {/* View Mode Switcher: List vs Grid */}
                  <div className="flex items-center bg-slate-100 p-1 rounded-2xl border border-slate-200 shrink-0">
                    <button
                      type="button"
                      onClick={() => setRoleViewMode('list')}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer border-none ${roleViewMode === 'list' ? 'bg-white text-slate-900 shadow-2xs font-extrabold' : 'text-slate-600 hover:text-slate-900'
                        }`}
                    >
                      <List className="w-3.5 h-3.5" />
                      <span>Senarai</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRoleViewMode('grid')}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer border-none ${roleViewMode === 'grid' ? 'bg-white text-slate-900 shadow-2xs font-extrabold' : 'text-slate-600 hover:text-slate-900'
                        }`}
                    >
                      <Grid className="w-3.5 h-3.5" />
                      <span>Grid</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* ROLES VIEW: LIST VIEW (TABLE) OR GRID VIEW */}
              {roleViewMode === 'list' ? (
                <div className="bg-white rounded-3xl border border-slate-200/90 shadow-2xs overflow-hidden">
                  <div className="overflow-x-auto no-scrollbar">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="bg-slate-50/90 text-slate-700 font-black border-b border-slate-200 text-[11px] uppercase tracking-wider">
                          <th className="py-3.5 px-4 min-w-[180px]">Nama Peranan</th>
                          <th className="py-3.5 px-4 min-w-[140px]">Kategori</th>
                          <th className="py-3.5 px-4 min-w-[260px]">Deskripsi Skop Tugas</th>
                          <th className="py-3.5 px-4 text-center whitespace-nowrap">Ahli Berdaftar</th>
                          <th className="py-3.5 px-4 text-center whitespace-nowrap">Hak Akses</th>
                          <th className="py-3.5 px-4 text-center whitespace-nowrap">Status</th>
                          <th className="py-3.5 px-4 text-right whitespace-nowrap">Tindakan</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredRoles.map(role => {
                          const assignedCount = roleMemberCounts[role.name] || 0;
                          const grantedPerms = rolePermsMap[role.name] || [];
                          const isExecutive = role.category === 'Majlis Tertinggi';

                          return (
                            <tr key={role.id} className="hover:bg-slate-50/70 transition-colors">
                              <td className="py-3.5 px-4 whitespace-nowrap">
                                <div className="font-black text-slate-900 text-xs flex items-center gap-1.5">
                                  <span>{role.name}</span>
                                  {isExecutive && (
                                    <Crown className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                                  )}
                                </div>
                                <div className="text-[10px] font-mono text-slate-400 mt-0.5">
                                  ID: {role.id}
                                </div>
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap">
                                <span className={`inline-block px-2.5 py-0.5 rounded-xl border text-[10px] ${getCategoryBadgeClass(role.category)}`}>
                                  {role.category}
                                </span>
                              </td>

                              <td className="py-3.5 px-4 text-slate-600 font-medium">
                                <p className="line-clamp-2 leading-relaxed">
                                  {role.description}
                                </p>
                              </td>

                              <td className="py-3.5 px-4 text-center whitespace-nowrap">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setMemberRoleFilter(role.name);
                                    setMemberSearchTerm('');
                                    setMemberOnlyMultiRoles(false);
                                    handleSwitchToMembersView();
                                  }}
                                  className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-slate-100 hover:bg-slate-200 font-extrabold text-slate-800 text-[11px] cursor-pointer border border-slate-200 transition-colors"
                                  title={`Lihat senarai ahli yang memegang peranan ${role.name}`}
                                >
                                  <Users className="w-3 h-3 text-slate-500" />
                                  <span>{assignedCount} Ahli</span>
                                </button>
                              </td>

                              <td className="py-3.5 px-4 text-center whitespace-nowrap">
                                <button
                                  type="button"
                                  onClick={() => handleSelectRoleForHakAkses(role.name)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl bg-red-50 text-asmm-red hover:bg-red-100 font-extrabold text-[11px] cursor-pointer border border-red-200 transition-colors"
                                  title="Buka Kawalan Hak Akses & Senarai Ahli Bagi Peranan Ini"
                                >
                                  <KeyRound className="w-3 h-3" />
                                  <span>{grantedPerms.length} Hak Akses</span>
                                </button>
                              </td>

                              <td className="py-3.5 px-4 text-center whitespace-nowrap">
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${role.isSystem ? 'bg-slate-100 text-slate-600' : 'bg-amber-100 text-amber-900 font-extrabold'
                                  }`}>
                                  {role.isSystem ? 'Sistem' : 'Tersuai'}
                                </span>
                              </td>

                              <td className="py-3.5 px-4 text-right whitespace-nowrap">
                                <div className="flex items-center justify-end gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => handleOpenEditRole(role)}
                                    className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-colors inline-flex items-center gap-1 cursor-pointer border border-slate-200"
                                    title="Sunting Peranan"
                                  >
                                    <Edit3 className="w-3.5 h-3.5 text-slate-600" />
                                    <span className="hidden sm:inline">Sunting</span>
                                  </button>
                                  {!role.isSystem && (
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteRole(role)}
                                      className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl transition-colors cursor-pointer border border-red-200"
                                      title="Padam Peranan"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredRoles.map(role => {
                    const assignedCount = roleMemberCounts[role.name] || 0;
                    const grantedPerms = rolePermsMap[role.name] || [];

                    return (
                      <div
                        key={role.id}
                        className="bg-white rounded-3xl border border-slate-200/90 shadow-2xs hover:shadow-sm transition-all p-5 flex flex-col justify-between gap-4"
                      >
                        <div className="space-y-3">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <span className={`inline-block px-2.5 py-0.5 rounded-xl border text-[10px] ${getCategoryBadgeClass(role.category)} mb-1.5`}>
                                {role.category}
                              </span>
                              <h4 className="text-base font-black text-slate-900 flex items-center gap-1.5">
                                <span>{role.name}</span>
                                {role.category === 'Majlis Tertinggi' && (
                                  <Crown className="w-4 h-4 text-amber-500 shrink-0" />
                                )}
                              </h4>
                            </div>

                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => handleOpenEditRole(role)}
                                className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg cursor-pointer border-none"
                                title="Sunting Peranan"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                              {!role.isSystem && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteRole(role)}
                                  className="p-1.5 text-red-400 hover:text-red-700 hover:bg-red-50 rounded-lg cursor-pointer border-none"
                                  title="Padam Peranan Tersuai"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>

                          <p className="text-xs text-slate-500 leading-relaxed min-h-[36px]">
                            {role.description}
                          </p>

                          <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-xs">
                            <button
                              type="button"
                              onClick={() => {
                                setMemberRoleFilter(role.name);
                                setMemberSearchTerm('');
                                setMemberOnlyMultiRoles(false);
                                handleSwitchToMembersView();
                              }}
                              className="flex items-center gap-1.5 text-slate-600 hover:text-slate-900 font-bold hover:underline cursor-pointer bg-transparent border-none p-0 text-xs"
                              title={`Lihat senarai ahli yang memegang peranan ${role.name}`}
                            >
                              <Users className="w-3.5 h-3.5 text-slate-400" />
                              <span>{assignedCount} Ahli Berdaftar</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => handleSelectRoleForHakAkses(role.name)}
                              className="text-[11px] font-extrabold text-asmm-red hover:underline flex items-center gap-1 cursor-pointer bg-transparent border-none"
                              title="Buka Kawalan Hak Akses & Senarai Ahli Bagi Peranan Ini"
                            >
                              <KeyRound className="w-3 h-3" />
                              <span>{grantedPerms.length} Hak Akses</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ========================================================================= */}
          {/* PAGEVIEW 2: KAWALAN HAK AKSES (PERMISSION CONTROL CRUD)                   */}
          {/* ========================================================================= */}
          {pageView === 'permissions' && (
            <div className="space-y-6 animate-fade-in">
              {/* Permission Filter & View Mode Bar */}
              <div className="bg-white p-4 rounded-3xl border border-slate-200 shadow-2xs flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3.5 top-2.5 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={permSearchTerm}
                    onChange={(e) => setPermSearchTerm(e.target.value)}
                    placeholder="Cari hak akses, nama modul, atau kata kunci..."
                    className="w-full pl-10 pr-9 py-2 rounded-2xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                  />
                  {permSearchTerm && (
                    <button
                      type="button"
                      onClick={() => setPermSearchTerm('')}
                      className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-wrap shrink-0">
                  <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-2xl border border-slate-200 text-xs">
                    <Filter className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="text-slate-500 font-bold hidden sm:inline">Modul:</span>
                    <select
                      value={permModuleFilter}
                      onChange={(e) => setPermModuleFilter(e.target.value)}
                      className="bg-transparent text-xs font-bold text-slate-800 focus:outline-none cursor-pointer border-none"
                    >
                      <option value="all">Semua Modul</option>
                      {uniqueModules.map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>

                  {/* View Mode Toggle: Matrix vs Inspector */}
                  <div className="flex items-center bg-slate-100 p-1 rounded-2xl border border-slate-200">
                    <button
                      type="button"
                      onClick={() => setPermViewMode('matrix')}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer border-none ${permViewMode === 'matrix' ? 'bg-white text-slate-900 shadow-2xs font-extrabold' : 'text-slate-600 hover:text-slate-900'
                        }`}
                    >
                      <Grid className="w-3.5 h-3.5" />
                      <span>Matriks</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPermViewMode('inspector')}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer border-none ${permViewMode === 'inspector' ? 'bg-white text-slate-900 shadow-2xs font-extrabold' : 'text-slate-600 hover:text-slate-900'
                        }`}
                    >
                      <Sliders className="w-3.5 h-3.5" />
                      <span>Ikut Peranan</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* VIEW MODE A: FULL INTERACTIVE PERMISSION MATRIX TABLE */}
              {permViewMode === 'matrix' && (
                <div className="bg-white rounded-3xl border border-slate-200/90 shadow-2xs overflow-hidden space-y-3 p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-base font-black text-slate-900">
                        Matriks Hak Akses & Kebenaran Portal ASMM
                      </h3>
                      <p className="text-xs text-slate-500">
                        Tandakan / nyahkan kotak pilihan untuk mengawal kebenaran capaian bagi setiap peranan serta-merta.
                      </p>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="bg-slate-50/90 text-slate-700 font-black border-b border-slate-200 text-[11px] uppercase tracking-wider">
                          <th className="py-3 px-4 sticky left-0 bg-slate-50 z-10 min-w-[240px]">
                            Modul & Hak Akses
                          </th>
                          {roles.map(r => (
                            <th key={r.name} className="py-3 px-2.5 text-center whitespace-nowrap min-w-[100px]">
                              <div className="font-extrabold text-slate-900">{r.name}</div>
                              <div className="text-[10px] text-slate-400 font-normal">
                                {(rolePermsMap[r.name] || []).length} aktif
                              </div>
                            </th>
                          ))}
                          <th className="py-3 px-3 text-right">Tindakan</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredPermissions.map(perm => (
                          <tr key={perm.key} className="hover:bg-slate-50/70 transition-colors">
                            <td className="py-3 px-4 sticky left-0 bg-white z-10">
                              <div className="font-black text-slate-900">{perm.name}</div>
                              <div className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5">
                                <span className="font-mono bg-slate-100 px-1.5 py-0.2 rounded text-slate-600">{perm.key}</span>
                                <span>• {perm.module}</span>
                              </div>
                              <div className="text-[11px] text-slate-500 mt-1 max-w-sm line-clamp-2">
                                {perm.description}
                              </div>
                            </td>

                            {roles.map(r => {
                              const isGranted = (rolePermsMap[r.name] || []).includes(perm.key);
                              return (
                                <td key={r.name} className="py-3 px-2.5 text-center whitespace-nowrap">
                                  <button
                                    type="button"
                                    onClick={() => handleTogglePermission(r.name, perm.key)}
                                    className={`w-7 h-7 rounded-xl flex items-center justify-center mx-auto transition-all cursor-pointer border ${isGranted
                                        ? 'bg-emerald-600 text-white border-emerald-700 shadow-2xs active:scale-95'
                                        : 'bg-slate-100 text-slate-300 border-slate-200 hover:border-slate-300'
                                      }`}
                                    title={`${isGranted ? 'Tarik balik' : 'Berikan'} hak akses "${perm.name}" kepada ${r.name}`}
                                  >
                                    {isGranted ? <Check className="w-4 h-4 stroke-[3]" /> : <X className="w-3.5 h-3.5" />}
                                  </button>
                                </td>
                              );
                            })}

                            <td className="py-3 px-3 text-right whitespace-nowrap">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleOpenEditPerm(perm)}
                                  className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg cursor-pointer border-none"
                                  title="Sunting Info Permission"
                                >
                                  <Edit3 className="w-3.5 h-3.5" />
                                </button>
                                {!perm.isSystem && (
                                  <button
                                    type="button"
                                    onClick={() => handleDeletePerm(perm)}
                                    className="p-1.5 text-red-400 hover:text-red-700 hover:bg-red-50 rounded-lg cursor-pointer border-none"
                                    title="Padam Custom Permission"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* VIEW MODE B: PERMISSION INSPECTOR BY SELECTED ROLE */}
              {permViewMode === 'inspector' && (
                <div className="bg-white rounded-3xl border border-slate-200/90 shadow-2xs p-5 sm:p-6 space-y-5">
                  {/* Role Picker Pills */}
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-2">
                      Pilih Peranan Untuk Dikawal:
                    </label>
                    <div className="flex items-center gap-1.5 overflow-x-auto pb-2">
                      {roles.map(r => (
                        <button
                          key={r.name}
                          type="button"
                          onClick={() => handleSelectRoleForHakAkses(r.name)}
                          className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer border ${selectedRoleForInspector === r.name
                              ? 'bg-asmm-red text-white border-asmm-red font-black shadow-xs'
                              : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                            }`}
                        >
                          <span>{r.name}</span>
                          <span className={`ml-1.5 text-[10px] px-1.5 py-0.2 rounded-full font-black ${selectedRoleForInspector === r.name ? 'bg-red-900 text-white' : 'bg-slate-200 text-slate-700'
                            }`}>
                            {(rolePermsMap[r.name] || []).length}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Action bar for selected role */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-200">
                    <div>
                      <h4 className="text-sm font-black text-slate-900 flex items-center gap-2">
                        <Shield className="w-4 h-4 text-asmm-red" />
                        <span>Kawalan Hak Akses: {selectedRoleForInspector}</span>
                      </h4>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Memiliki {(rolePermsMap[selectedRoleForInspector] || []).length} daripada {permissions.length} jumlah hak akses portal.
                      </p>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => handleGrantAllForRole(selectedRoleForInspector)}
                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl cursor-pointer border-none transition-all shadow-2xs"
                      >
                        Beri Semua
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRevokeAllForRole(selectedRoleForInspector)}
                        className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold rounded-xl cursor-pointer border-none transition-all"
                      >
                        Kosongkan Semua
                      </button>
                    </div>
                  </div>

                  {/* Toggles list grouped by module */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                    {filteredPermissions.map(perm => {
                      const isGranted = (rolePermsMap[selectedRoleForInspector] || []).includes(perm.key);

                      return (
                        <div
                          key={perm.key}
                          onClick={() => handleTogglePermission(selectedRoleForInspector, perm.key)}
                          className={`p-4 rounded-2xl border transition-all cursor-pointer flex items-start justify-between gap-3 select-none ${isGranted
                              ? 'bg-emerald-50/60 border-emerald-200 text-slate-900'
                              : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                            }`}
                        >
                          <div className="space-y-1 min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className={`font-black text-xs ${isGranted ? 'text-emerald-950' : 'text-slate-800'}`}>
                                {perm.name}
                              </span>
                              <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-slate-100 text-slate-600">
                                {perm.module}
                              </span>
                            </div>
                            <p className="text-[11px] text-slate-500 leading-relaxed">
                              {perm.description}
                            </p>
                          </div>

                          <div className="shrink-0 mt-0.5">
                            {isGranted ? (
                              <div className="w-6 h-6 bg-emerald-600 text-white rounded-lg flex items-center justify-center shadow-2xs">
                                <Check className="w-3.5 h-3.5 stroke-[3]" />
                              </div>
                            ) : (
                              <div className="w-6 h-6 bg-slate-100 text-slate-300 rounded-lg flex items-center justify-center border border-slate-200">
                                <X className="w-3.5 h-3.5" />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* ========================================================================= */}
                  {/* ROW BELOW: SENARAI AHLI MEMEGANG PERANAN INI                             */}
                  {/* ========================================================================= */}
                  <div className="pt-6 border-t border-slate-200 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-red-50 text-asmm-red border border-red-100 flex items-center justify-center shrink-0">
                          <Users className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="text-sm font-black text-slate-900 flex items-center gap-2 flex-wrap">
                            <span>Senarai Ahli Memegang Peranan:</span>
                            <span className="text-asmm-red bg-red-50 px-2.5 py-0.5 rounded-xl border border-red-200 font-black">
                              {selectedRoleForInspector}
                            </span>
                          </h4>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {assignedMembersForSelectedRole.length} ahli & pesilat persatuan sedang aktif dilantik memegang peranan ini.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 border border-slate-200 text-xs font-black text-slate-800">
                          <Users className="w-3.5 h-3.5 text-slate-500" />
                          <span>{assignedMembersForSelectedRole.length} Ahli</span>
                        </span>
                        <button
                          type="button"
                          onClick={handleOpenQuickAssign}
                          className="px-3.5 py-1.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl shadow-2xs flex items-center gap-1.5 cursor-pointer border-none transition-all active:scale-95"
                          title={`Lantik ahli baharu ke peranan ${selectedRoleForInspector}`}
                        >
                          <Plus className="w-3.5 h-3.5" />
                          <span>Lantik Ahli</span>
                        </button>
                      </div>
                    </div>

                    {/* Quick Search if more than 3 members */}
                    {assignedMembersForSelectedRole.length > 3 && (
                      <div className="relative">
                        <Search className="absolute left-3.5 top-2.5 w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          value={assignedSearchTerm}
                          onChange={(e) => setAssignedSearchTerm(e.target.value)}
                          placeholder={`Cari nama, No. KP, atau gelanggang ahli dalam peranan ${selectedRoleForInspector}...`}
                          className="w-full pl-10 pr-9 py-2 rounded-2xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                        />
                        {assignedSearchTerm && (
                          <button
                            type="button"
                            onClick={() => setAssignedSearchTerm('')}
                            className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 cursor-pointer"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    )}

                    {/* Empty State */}
                    {assignedMembersForSelectedRole.length === 0 ? (
                      <div className="py-10 px-4 text-center flex flex-col items-center justify-center space-y-3 bg-slate-50/60 rounded-2xl border border-dashed border-slate-200">
                        <div className="w-12 h-12 rounded-2xl bg-white text-slate-400 border border-slate-200 flex items-center justify-center shadow-2xs">
                          <Users className="w-6 h-6 text-slate-400" />
                        </div>
                        <div className="space-y-1">
                          <h5 className="text-xs font-black text-slate-800">
                            Tiada Ahli Memegang Peranan "{selectedRoleForInspector}"
                          </h5>
                          <p className="text-[11px] text-slate-500 max-w-sm">
                            Belum ada mana-mana ahli yang ditetapkan di bawah peranan ini. Klik butang di bawah untuk melantik ahli sekarang.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={handleOpenQuickAssign}
                          className="px-4 py-2 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-bold rounded-xl transition-all cursor-pointer border-none shadow-2xs flex items-center gap-1.5"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          <span>Lantik Ahli Pertama Sekarang</span>
                        </button>
                      </div>
                    ) : filteredAssignedMembers.length === 0 ? (
                      <div className="py-8 text-center text-xs text-slate-500 bg-slate-50/50 rounded-2xl border border-slate-100">
                        Tiada ahli sepadan dengan carian "{assignedSearchTerm}".
                      </div>
                    ) : (
                      <div className="overflow-x-auto rounded-2xl border border-slate-200">
                        <table className="w-full text-left border-collapse text-xs">
                          <thead>
                            <tr className="bg-slate-50 text-slate-700 font-black border-b border-slate-200 text-[11px] uppercase tracking-wider">
                              <th className="py-3 px-4">Nama Ahli / Pesilat</th>
                              <th className="py-3 px-4">Pusat Gelanggang</th>
                              <th className="py-3 px-4">Bengkung & Silibus</th>
                              <th className="py-3 px-4">Semua Peranan</th>
                              <th className="py-3 px-4 text-center">Status</th>
                              <th className="py-3 px-4 text-right">Tindakan</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 bg-white">
                            {filteredAssignedMembers.map(m => {
                              const memberRoles: Role[] = (m.roles && m.roles.length > 0) ? m.roles : [m.role || 'Ahli'];
                              return (
                                <tr key={m.id} className="hover:bg-slate-50/70 transition-colors">
                                  <td className="py-3 px-4">
                                    <div className="flex items-center gap-3">
                                      <div className="w-9 h-9 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden shrink-0 flex items-center justify-center">
                                        {m.photoUrl && m.photoUrl !== DEFAULT_AVATAR_URL ? (
                                          <img
                                            src={m.photoUrl}
                                            alt={m.name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                              (e.target as HTMLElement).style.display = 'none';
                                            }}
                                          />
                                        ) : (
                                          <span className="text-xs font-black text-slate-600">
                                            {m.name.charAt(0).toUpperCase()}
                                          </span>
                                        )}
                                      </div>
                                      <div className="min-w-0">
                                        <div className="font-bold text-slate-900 truncate">{m.name}</div>
                                        <div className="text-[10px] font-mono text-slate-400 flex items-center gap-1.5 flex-wrap">
                                          <span className="font-bold text-slate-700 bg-slate-100 px-1 rounded border border-slate-200 text-[10px]">
                                            {sanitizeMemberId(m.id)}
                                          </span>
                                          {m.icNo && (
                                            <>
                                              <span>•</span>
                                              <span>{m.icNo}</span>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </td>

                                  <td className="py-3 px-4 whitespace-nowrap text-slate-700 font-medium">
                                    <div className="flex items-center gap-1.5">
                                      <Building className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                      <span>{m.gelanggang || 'Semua Gelanggang'}</span>
                                    </div>
                                  </td>

                                  <td className="py-3 px-4 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-bold text-slate-800">
                                        {getSilatRankFromBengkung(m.bengkung || 'Putih')}
                                      </span>
                                    </div>
                                  </td>

                                  <td className="py-3 px-4">
                                    <div className="flex items-center gap-1.5 flex-wrap max-w-xs">
                                      {memberRoles.map(r => {
                                        const isInspected = r === selectedRoleForInspector;
                                        const isPrimary = r === m.role;
                                        return (
                                          <span
                                            key={r}
                                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-extrabold border ${
                                              isInspected
                                                ? 'bg-red-50 text-asmm-red border-red-200 shadow-2xs font-black'
                                                : 'bg-slate-100 text-slate-700 border-slate-200'
                                            }`}
                                          >
                                            <span>{r}</span>
                                            {isPrimary && (
                                              <span title="Peranan Utama" className="inline-flex items-center">
                                                <Star className="w-3 h-3 text-amber-500 fill-amber-400 shrink-0" />
                                              </span>
                                            )}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  </td>

                                  <td className="py-3 px-4 text-center whitespace-nowrap">
                                    <span className={`text-[10px] font-extrabold px-2.5 py-0.5 rounded-full ${m.status === 'Aktif'
                                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                        : 'bg-slate-100 text-slate-600 border border-slate-200'
                                      }`}>
                                      {m.status || 'Aktif'}
                                    </span>
                                  </td>

                                  <td className="py-3 px-4 text-right whitespace-nowrap">
                                    <div className="flex items-center justify-end gap-1.5">
                                      <button
                                        type="button"
                                        onClick={() => handleOpenAssignModal(m)}
                                        className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-colors inline-flex items-center gap-1 cursor-pointer border border-slate-200"
                                        title="Urus peranan ahli ini (tambah / tukar peranan)"
                                      >
                                        <Edit3 className="w-3 h-3 text-slate-600" />
                                        <span>Urus Peranan</span>
                                      </button>
                                      {memberRoles.length > 1 && (
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveRoleFromMember(m, selectedRoleForInspector)}
                                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors cursor-pointer border border-red-200"
                                          title={`Gugurkan peranan "${selectedRoleForInspector}" daripada ahli ini`}
                                        >
                                          <X className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ========================================================================= */}
          {/* PAGEVIEW 3: URUS PERANAN AHLI (MULTI-ROLES MANAGEMENT DEDICATED PAGE VIEW) */}
          {/* ========================================================================= */}
          {pageView === 'members' && (
            selectedMemberForRole ? (
              /* SUB-VIEW B: DEDICATED FULL-PAGE MEMBER MULTI-ROLE EDITOR (ZERO MODAL FLOW) */
              <div className="space-y-6 animate-fade-in">
                {/* Top Return Banner */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-3xl border border-slate-200/90 shadow-2xs">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setSelectedMemberForRole(null)}
                      className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-black flex items-center gap-2 cursor-pointer border border-slate-200 transition-all active:scale-95"
                    >
                      <ArrowLeft className="w-4 h-4 text-slate-600" />
                      <span>Kembali ke Direktori Ahli</span>
                    </button>
                    <div className="hidden sm:block h-5 w-px bg-slate-200" />
                    <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500">
                      <span>Urus Peranan Ahli</span>
                      <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                      <span className="font-bold text-slate-900">{selectedMemberForRole.name}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 self-end sm:self-auto">
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Mod Kemaskini:</span>
                    <span className="px-2.5 py-1 bg-red-50 text-asmm-red border border-red-200 rounded-lg text-[11px] font-black">
                      Pelbagai Peranan (Multi-Roles)
                    </span>
                  </div>
                </div>

                {/* Member Profile Hero Card */}
                <div className="bg-white rounded-3xl border border-slate-200/90 shadow-2xs p-6 sm:p-7 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-bl from-red-50/60 to-transparent rounded-bl-full pointer-events-none" />

                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 relative z-10">
                    <img
                      src={selectedMemberForRole.photoUrl || DEFAULT_AVATAR_URL}
                      alt={selectedMemberForRole.name}
                      onError={(e) => { (e.target as HTMLElement).setAttribute('src', DEFAULT_AVATAR_URL); }}
                      className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover border-2 border-slate-200 shrink-0 shadow-sm"
                    />

                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black bg-blue-50 text-blue-700 border border-blue-200">
                          {selectedMemberForRole.status || 'Aktif'}
                        </span>
                        <span className="font-mono text-[11px] font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200">
                          ID: {sanitizeMemberId(selectedMemberForRole.id)}
                        </span>
                        {selectedMemberForRole.icNo && (
                          <span className="text-[11px] font-mono text-slate-500">
                            IC: {selectedMemberForRole.icNo}
                          </span>
                        )}
                      </div>

                      <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                        {selectedMemberForRole.name}
                      </h2>

                      <div className="flex items-center gap-3 flex-wrap text-xs text-slate-600">
                        <div className="flex items-center gap-1.5 font-bold">
                          <Building className="w-3.5 h-3.5 text-slate-400" />
                          <span>{selectedMemberForRole.gelanggang || 'Semua Gelanggang'}</span>
                        </div>
                        <span>•</span>
                        <div className="flex items-center gap-1.5 font-bold">
                          <Award className="w-3.5 h-3.5 text-amber-500" />
                          <span>{getSilatRankFromBengkung(selectedMemberForRole.bengkung || 'Putih')}</span>
                        </div>
                        <span>•</span>
                        <span className="text-slate-500 text-[11px]">
                          {selectedRolesForMember.length} Peranan Dipilih
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSaveMemberRole} className="space-y-6">
                  {/* Selected Roles Chip Box */}
                  <div className="bg-white rounded-3xl border border-slate-200/90 shadow-2xs p-6 space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
                      <div>
                        <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                          <ShieldCheck className="w-4 h-4 text-asmm-red" />
                          <span>Senarai Peranan Dipilih ({selectedRolesForMember.length})</span>
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Ahli akan menerima semua gabungan kebenaran hak akses portal bagi setiap peranan di bawah.
                        </p>
                      </div>
                      <span className="text-[11px] text-slate-400">Klik butang pangkah (✕) untuk membuang peranan</span>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap pt-1 min-h-[44px]">
                      {selectedRolesForMember.map(r => (
                        <div
                          key={r}
                          className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl border text-xs font-bold shadow-2xs transition-all ${
                            r === primaryRoleForMember
                              ? 'bg-red-50 text-asmm-red border-red-300 ring-1 ring-asmm-red/30'
                              : 'bg-white text-slate-800 border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <Shield className={`w-3.5 h-3.5 ${r === primaryRoleForMember ? 'text-asmm-red' : 'text-slate-400'}`} />
                          <span className="font-extrabold">{r}</span>
                          {r === primaryRoleForMember && (
                            <span title="Peranan Utama" className="inline-flex items-center">
                              <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400 shrink-0" />
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => handleToggleRoleInModal(r)}
                            className="text-slate-400 hover:text-red-600 transition-colors cursor-pointer border-none bg-transparent p-0 flex items-center ml-1"
                            title={`Keluarkan peranan ${r}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Catalog of Roles Grouped by Official Category */}
                  <div className="bg-white rounded-3xl border border-slate-200/90 shadow-2xs p-6 space-y-6">
                    <div>
                      <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                        <Layers className="w-4 h-4 text-asmm-red" />
                        <span>Katalog Peranan Rasmi PSSASMM</span>
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Klik pada mana-mana kad peranan untuk menambah atau menggugurkan peranan daripada senarai ahli ini.
                      </p>
                    </div>

                    <div className="space-y-6">
                      {(['Majlis Tertinggi', 'Pengurusan Gelanggang', 'Kejurulatihan', 'Jawatankuasa & Biro', 'Keahlian Am'] as const).map(cat => {
                        const catRoles = roles.filter(r => r.category === cat);
                        if (catRoles.length === 0) return null;

                        return (
                          <div key={cat} className="space-y-3">
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">
                                {cat}
                              </span>
                              <div className="flex-1 h-px bg-slate-100" />
                              <span className="text-[10px] font-bold text-slate-400">
                                {catRoles.length} peranan
                              </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {catRoles.map(r => {
                                const isSelected = selectedRolesForMember.includes(r.name as Role);
                                const isPrimary = r.name === primaryRoleForMember;
                                const permCount = (rolePermsMap[r.name] || []).length;

                                return (
                                  <button
                                    key={r.id}
                                    type="button"
                                    onClick={() => handleToggleRoleInModal(r.name as Role)}
                                    className={`p-4 rounded-2xl border text-left transition-all flex flex-col justify-between gap-3 cursor-pointer group ${
                                      isSelected
                                        ? 'bg-red-50/50 border-asmm-red text-slate-900 shadow-xs ring-2 ring-asmm-red/20'
                                        : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50/50'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <h4 className="text-xs font-black text-slate-900 group-hover:text-asmm-red transition-colors">
                                            {r.name}
                                          </h4>
                                          {isPrimary && (
                                            <span title="Peranan Utama" className="inline-flex items-center">
                                              <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400 shrink-0" />
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-[11px] text-slate-500 line-clamp-2 mt-1 leading-relaxed">
                                          {r.description || 'Tiada huraian rasmi bagi peranan ini.'}
                                        </p>
                                      </div>

                                      <div className={`w-5 h-5 rounded-lg flex items-center justify-center shrink-0 transition-all ${
                                        isSelected
                                          ? 'bg-asmm-red text-white shadow-2xs'
                                          : 'border border-slate-300 bg-white group-hover:border-slate-400'
                                      }`}>
                                        {isSelected && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                                      </div>
                                    </div>

                                    <div className="flex items-center justify-between pt-2 border-t border-slate-100/80 text-[10px] text-slate-500 font-bold">
                                      <span className="flex items-center gap-1">
                                        <KeyRound className="w-3 h-3 text-slate-400" />
                                        <span>{permCount} Hak Akses</span>
                                      </span>
                                      <span className={isSelected ? 'text-asmm-red font-extrabold' : 'text-slate-400'}>
                                        {isSelected ? '✓ Terpilih' : '+ Klik Tambah'}
                                      </span>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Primary Role & Gelanggang Configuration Card */}
                  <div className="bg-white rounded-3xl border border-slate-200/90 shadow-2xs p-6 space-y-4">
                    <h3 className="text-sm font-black text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-3">
                      <Settings className="w-4 h-4 text-asmm-red" />
                      <span>Konfigurasi Peranan Utama & Gelanggang</span>
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Primary Role Selector */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          Peranan Utama (Paparan Kad & Profil) <span className="text-asmm-red">*</span>
                        </label>
                        <select
                          value={primaryRoleForMember}
                          onChange={(e) => setPrimaryRoleForMember(e.target.value as Role)}
                          className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-bold"
                          required
                        >
                          {selectedRolesForMember.map(r => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                        <p className="text-[10px] text-slate-500 mt-1">
                          Peranan utama dipaparkan sebagai gelaran jawatan utama pada kad keahlian rasmi pesilat.
                        </p>
                      </div>

                      {/* Gelanggang Selector */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          Pusat Gelanggang Latihan
                        </label>
                        <select
                          value={newGelanggang}
                          onChange={(e) => setNewGelanggang(e.target.value as Gelanggang)}
                          className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-bold"
                        >
                          <option value="Pusaka Warisan">Pusaka Warisan</option>
                          <option value="Wadi AnNour">Wadi AnNour</option>
                          <option value="Paya Besar">Paya Besar</option>
                          <option value="Semua Gelanggang">Semua Gelanggang (Ibu Pejabat PSSASMM)</option>
                        </select>
                        <p className="text-[10px] text-slate-500 mt-1">
                          Pusat gelanggang latihan utama di mana ahli bergiat aktif.
                        </p>
                      </div>
                    </div>

                    {/* Notes / Reference */}
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        Catatan / Rujukan Mesyuarat Pelantikan (Pilihan)
                      </label>
                      <input
                        type="text"
                        value={roleNotes}
                        onChange={(e) => setRoleNotes(e.target.value)}
                        placeholder="Contoh: Lantikan Mesyuarat Agung Tahunan 2026 / Surat Pelantikan No. 04/2026"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                      />
                    </div>
                  </div>

                  {/* Sticky Action Footer */}
                  <div className="bg-white p-4 rounded-2xl border border-slate-200/90 shadow-2xs flex flex-col sm:flex-row items-center justify-between gap-3 sticky bottom-4 z-20">
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <span className="font-bold text-slate-900">{selectedRolesForMember.length}</span> peranan dipilih
                      <span>•</span>
                      <span>Peranan Utama: <strong className="text-slate-900">{primaryRoleForMember}</strong></span>
                    </div>

                    <div className="flex items-center gap-2.5 w-full sm:w-auto justify-end">
                      <button
                        type="button"
                        onClick={() => setSelectedMemberForRole(null)}
                        disabled={isSubmittingRoleChange}
                        className="px-4 py-2.5 text-xs font-bold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-xl cursor-pointer border-none transition-all active:scale-95"
                      >
                        Batal
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmittingRoleChange}
                        className="px-6 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-black text-xs rounded-xl shadow-xs transition-all flex items-center gap-2 cursor-pointer border-none disabled:opacity-50 active:scale-95"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        <span>{isSubmittingRoleChange ? 'Menyimpan ke Pangkalan Data...' : 'Simpan Semua Peranan Ahli'}</span>
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            ) : (
              /* SUB-VIEW A: DIREKTORI PERANAN AHLI (MULTI-ROLES DIRECTORY & MANAGEMENT) */
              <div className="space-y-6 animate-fade-in">
                {/* Top KPI Cards for Member Multi-Roles */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                  <div className="bg-white p-4 rounded-3xl border border-slate-200/90 shadow-2xs space-y-1">
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Jumlah Ahli Aktif</span>
                    <div className="text-2xl font-black text-slate-900">{memberKpis.total}</div>
                    <p className="text-[11px] text-slate-500">Ahli berdaftar dalam sistem</p>
                  </div>

                  <div className="bg-white p-4 rounded-3xl border border-slate-200/90 shadow-2xs space-y-1 border-l-4 border-l-asmm-red">
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Ahli Multi-Roles</span>
                    <div className="text-2xl font-black text-asmm-red">{memberKpis.multiRole}</div>
                    <p className="text-[11px] text-slate-500">Memegang lebih dari 1 peranan</p>
                  </div>

                  <div className="bg-white p-4 rounded-3xl border border-slate-200/90 shadow-2xs space-y-1 border-l-4 border-l-amber-500">
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Majlis & Pentadbiran</span>
                    <div className="text-2xl font-black text-slate-900">{memberKpis.leadership}</div>
                    <p className="text-[11px] text-slate-500">Pemegang amanah kepimpinan</p>
                  </div>

                  <div className="bg-white p-4 rounded-3xl border border-slate-200/90 shadow-2xs space-y-1 border-l-4 border-l-blue-600">
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Tenaga Pengajar</span>
                    <div className="text-2xl font-black text-slate-900">{memberKpis.trainer}</div>
                    <p className="text-[11px] text-slate-500">Guru utama & jurulatih</p>
                  </div>
                </div>

                {/* Filter & Search Toolbar */}
                <div className="bg-white p-4 rounded-3xl border border-slate-200 shadow-2xs flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3.5 top-2.5 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={memberSearchTerm}
                      onChange={(e) => setMemberSearchTerm(e.target.value)}
                      placeholder="Cari nama ahli, No. IC, ID pesilat, peranan..."
                      className="w-full pl-10 pr-9 py-2 rounded-2xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                    />
                    {memberSearchTerm && (
                      <button
                        type="button"
                        onClick={() => setMemberSearchTerm('')}
                        className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 cursor-pointer"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-wrap shrink-0">
                    {/* Role Filter */}
                    <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-2xl border border-slate-200 text-xs">
                      <Filter className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="text-slate-500 font-bold hidden sm:inline">Peranan:</span>
                      <select
                        value={memberRoleFilter}
                        onChange={(e) => setMemberRoleFilter(e.target.value)}
                        className="bg-transparent text-xs font-bold text-slate-800 focus:outline-none cursor-pointer border-none"
                      >
                        <option value="all">Semua Peranan ({roles.length})</option>
                        {roles.map(r => (
                          <option key={r.id} value={r.name}>{r.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Gelanggang Filter */}
                    <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-2xl border border-slate-200 text-xs">
                      <Building className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="text-slate-500 font-bold hidden sm:inline">Gelanggang:</span>
                      <select
                        value={memberGelanggangFilter}
                        onChange={(e) => setMemberGelanggangFilter(e.target.value)}
                        className="bg-transparent text-xs font-bold text-slate-800 focus:outline-none cursor-pointer border-none"
                      >
                        <option value="all">Semua Gelanggang</option>
                        <option value="Pusaka Warisan">Pusaka Warisan</option>
                        <option value="Wadi AnNour">Wadi AnNour</option>
                        <option value="Paya Besar">Paya Besar</option>
                        <option value="Semua Gelanggang">Semua Gelanggang (HQ)</option>
                      </select>
                    </div>

                    {/* Multi-Role Only Toggle */}
                    <button
                      type="button"
                      onClick={() => setMemberOnlyMultiRoles(!memberOnlyMultiRoles)}
                      className={`px-3 py-1.5 rounded-2xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer border ${
                        memberOnlyMultiRoles
                          ? 'bg-red-50 text-asmm-red border-asmm-red shadow-2xs font-extrabold'
                          : 'bg-slate-50 text-slate-600 border-slate-200 hover:text-slate-900'
                      }`}
                    >
                      <ShieldCheck className="w-3.5 h-3.5" />
                      <span>Hanya Multi-Roles</span>
                      <span className={`px-1.5 py-0.2 rounded-md text-[10px] font-black ${
                        memberOnlyMultiRoles ? 'bg-asmm-red text-white' : 'bg-slate-200 text-slate-700'
                      }`}>
                        {memberKpis.multiRole}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Member Table */}
                <div className="bg-white rounded-3xl border border-slate-200 shadow-2xs overflow-hidden">
                  <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                    <div>
                      <h3 className="text-xs font-black uppercase tracking-wider text-slate-700">
                        Direktori Ahli & Peranan Rasmi ({filteredMembersForMultiRoles.length})
                      </h3>
                      <p className="text-[11px] text-slate-500">
                        Klik butang "Urus Peranan" pada mana-mana ahli untuk menambah, menggugurkan, atau menukar peranan utama.
                      </p>
                    </div>

                    {(memberSearchTerm || memberRoleFilter !== 'all' || memberGelanggangFilter !== 'all' || memberOnlyMultiRoles) && (
                      <button
                        type="button"
                        onClick={() => {
                          setMemberSearchTerm('');
                          setMemberRoleFilter('all');
                          setMemberGelanggangFilter('all');
                          setMemberOnlyMultiRoles(false);
                        }}
                        className="text-xs font-bold text-asmm-red hover:underline cursor-pointer border-none bg-transparent"
                      >
                        Set Semula Penapis
                      </button>
                    )}
                  </div>

                  {filteredMembersForMultiRoles.length === 0 ? (
                    <div className="p-12 text-center space-y-3">
                      <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto text-slate-400">
                        <Users className="w-6 h-6" />
                      </div>
                      <h4 className="text-sm font-black text-slate-800">Tiada Ahli Dijumpai</h4>
                      <p className="text-xs text-slate-500 max-w-sm mx-auto">
                        Tiada rekod ahli menepati carian atau penapis yang dipilih. Sila cuba kata kunci lain.
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse text-xs">
                        <thead>
                          <tr className="bg-slate-50/70 border-b border-slate-200/80 text-[10px] uppercase font-bold text-slate-500 tracking-wider">
                            <th className="py-3 px-4">Pesilat / Ahli</th>
                            <th className="py-3 px-4">Pusat Gelanggang</th>
                            <th className="py-3 px-4">Bengkung & Silibus</th>
                            <th className="py-3 px-4">Peranan Dipegang (Multi-Roles)</th>
                            <th className="py-3 px-4 text-right">Tindakan</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {filteredMembersForMultiRoles.map(m => {
                            const memberRoles: Role[] = (m.roles && m.roles.length > 0) ? m.roles : [m.role || 'Ahli'];
                            const isMulti = memberRoles.length > 1;

                            return (
                              <tr key={m.id} className="hover:bg-slate-50/80 transition-colors">
                                <td className="py-3.5 px-4">
                                  <div className="flex items-center gap-3">
                                    <img
                                      src={m.photoUrl || DEFAULT_AVATAR_URL}
                                      alt={m.name}
                                      onError={(e) => { (e.target as HTMLElement).setAttribute('src', DEFAULT_AVATAR_URL); }}
                                      className="w-10 h-10 rounded-xl object-cover border border-slate-200 shrink-0 shadow-2xs"
                                    />
                                    <div className="min-w-0">
                                      <div className="font-extrabold text-slate-900 truncate flex items-center gap-1.5">
                                        <span>{m.name}</span>
                                        {isMulti && (
                                          <span className="px-1.5 py-0.2 rounded-md bg-amber-50 text-amber-900 border border-amber-300 text-[9px] font-black shrink-0">
                                            Multi-Roles ({memberRoles.length})
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-[10px] font-mono text-slate-400 flex items-center gap-1.5 flex-wrap mt-0.5">
                                        <span className="font-bold text-slate-700 bg-slate-100 px-1 rounded border border-slate-200 text-[10px]">
                                          {sanitizeMemberId(m.id)}
                                        </span>
                                        {m.icNo && (
                                          <>
                                            <span>•</span>
                                            <span>{m.icNo}</span>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </td>

                                <td className="py-3.5 px-4 whitespace-nowrap text-slate-700 font-medium">
                                  <div className="flex items-center gap-1.5">
                                    <Building className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                    <span>{m.gelanggang || 'Semua Gelanggang'}</span>
                                  </div>
                                </td>

                                <td className="py-3.5 px-4 whitespace-nowrap">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold text-slate-800">
                                      {getSilatRankFromBengkung(m.bengkung || 'Putih')}
                                    </span>
                                  </div>
                                </td>

                                <td className="py-3.5 px-4">
                                  <div className="flex items-center gap-1.5 flex-wrap max-w-sm">
                                    {memberRoles.map(r => {
                                      const isPrimary = r === m.role;
                                      return (
                                        <span
                                          key={r}
                                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-[10px] font-extrabold border ${
                                            isPrimary
                                              ? 'bg-red-50 text-asmm-red border-red-200 shadow-2xs font-black'
                                              : 'bg-slate-100 text-slate-700 border-slate-200'
                                          }`}
                                        >
                                          <span>{r}</span>
                                          {isPrimary && (
                                            <span title="Peranan Utama" className="inline-flex items-center">
                                              <Star className="w-3 h-3 text-amber-500 fill-amber-400 shrink-0" />
                                            </span>
                                          )}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </td>

                                <td className="py-3.5 px-4 text-right whitespace-nowrap">
                                  <button
                                    type="button"
                                    onClick={() => handleOpenAssignPageView(m)}
                                    className="px-3.5 py-1.5 bg-slate-900 hover:bg-black text-white font-extrabold rounded-xl text-xs transition-all inline-flex items-center gap-1.5 cursor-pointer border-none shadow-2xs active:scale-95"
                                  >
                                    <Edit3 className="w-3.5 h-3.5" />
                                    <span>Urus Peranan</span>
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )
          )}
        </>
      )}

      {/* ========================================================================= */}
      {/* MODAL 1: ADD / EDIT ROLE                                                 */}
      {/* ========================================================================= */}
      {isRoleModalOpen && (
        <Modal
          isOpen={isRoleModalOpen}
          onClose={() => setIsRoleModalOpen(false)}
          title={editingRole ? `Kemaskini Peranan` : 'Tambah Peranan'}
          maxWidth="max-w-lg"
        >
          <form onSubmit={handleSaveRole} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Nama Peranan *
              </label>
              <input
                type="text"
                value={roleFormName}
                onChange={(e) => setRoleFormName(e.target.value)}
                placeholder="Contoh: Ketua Biro Disiplin & Keselamatan"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-bold"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Kategori Peranan *
              </label>
              <select
                value={roleFormCategory}
                onChange={(e) => setRoleFormCategory(e.target.value as any)}
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-bold"
              >
                <option value="Majlis Tertinggi">Majlis Tertinggi</option>
                <option value="Pengurusan Gelanggang">Pengurusan Gelanggang</option>
                <option value="Jawatankuasa & Biro">Jawatankuasa & Biro</option>
                <option value="Kejurulatihan">Kejurulatihan</option>
                <option value="Keahlian Am">Keahlian Am</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Deskripsi / Tanggungjawab Tugas
              </label>
              <textarea
                value={roleFormDesc}
                onChange={(e) => setRoleFormDesc(e.target.value)}
                rows={3}
                placeholder="Ringkasan skop tugas jawatan mengikut ketetapan pertubuhan..."
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
              />
            </div>

            {!editingRole && (
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Salin Hak Akses Asas (Pilihan)
                </label>
                <select
                  value={roleFormCloneFrom}
                  onChange={(e) => setRoleFormCloneFrom(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                >
                  <option value="">-- Tiada (Mula Tanpa Hak Akses) --</option>
                  {roles.map(r => (
                    <option key={r.name} value={r.name}>Salin daripada: {r.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex justify-end gap-2.5 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setIsRoleModalOpen(false)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 bg-slate-100 rounded-xl cursor-pointer border-none"
              >
                Batal
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-asmm-red hover:bg-asmm-crimson text-white font-black text-xs rounded-xl shadow-sm transition-all flex items-center gap-1.5 cursor-pointer border-none active:scale-95"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>{editingRole ? 'Kemaskini Peranan' : 'Daftar Peranan Baharu'}</span>
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ========================================================================= */}
      {/* MODAL 2: ADD / EDIT PERMISSION                                           */}
      {/* ========================================================================= */}
      {isPermModalOpen && (
        <Modal
          isOpen={isPermModalOpen}
          onClose={() => setIsPermModalOpen(false)}
          title={editingPerm ? `Kemaskini Akses` : 'Tambah Akses'}
          maxWidth="max-w-lg"
        >
          <form onSubmit={handleSavePerm} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Kod Akses *
              </label>
              <input
                type="text"
                value={permFormKey}
                onChange={(e) => setPermFormKey(e.target.value)}
                disabled={!!editingPerm}
                placeholder="Contoh: cetak_sijil_bengkung"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs font-mono text-slate-900 font-bold disabled:opacity-50"
                required
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Gunakan huruf kecil dan garis bawah (_) tanpa ruang.
              </p>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Nama Akses *
              </label>
              <input
                type="text"
                value={permFormName}
                onChange={(e) => setPermFormName(e.target.value)}
                placeholder="Contoh: Cetak Sijil Bengkung Rasmi"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-bold"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Modul Berkaitan *
              </label>
              <input
                type="text"
                value={permFormModule}
                onChange={(e) => setPermFormModule(e.target.value)}
                placeholder="Contoh: Latihan & Silibus"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-bold"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Penerangan Skop Hak Akses
              </label>
              <textarea
                value={permFormDesc}
                onChange={(e) => setPermFormDesc(e.target.value)}
                rows={3}
                placeholder="Menjelaskan kebenaran yang dibolehkan bagi hak akses ini..."
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
              />
            </div>

            <div className="flex justify-end gap-2.5 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setIsPermModalOpen(false)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 bg-slate-100 rounded-xl cursor-pointer border-none"
              >
                Batal
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-asmm-red hover:bg-asmm-crimson text-white font-black text-xs rounded-xl shadow-sm transition-all flex items-center gap-1.5 cursor-pointer border-none active:scale-95"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>{editingPerm ? 'Kemaskini Permission' : 'Simpan Permission'}</span>
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ========================================================================= */}
      {/* MODAL: QUICK ASSIGN MEMBER TO SELECTED ROLE                              */}
      {/* ========================================================================= */}
      {isQuickAssignModalOpen && (
        <Modal
          isOpen={isQuickAssignModalOpen}
          onClose={() => setIsQuickAssignModalOpen(false)}
          title={`Lantik Ahli ke Peranan: ${selectedRoleForInspector}`}
          maxWidth="max-w-md"
        >
          <form onSubmit={handleSaveQuickAssign} className="space-y-4">
            <div className="p-3 bg-red-50/60 rounded-2xl border border-red-100 flex items-start gap-2.5">
              <ShieldCheck className="w-4 h-4 text-asmm-red shrink-0 mt-0.5" />
              <div className="text-xs text-slate-700 leading-relaxed">
                Melantik ahli ke peranan <strong className="text-slate-900">{selectedRoleForInspector}</strong> akan memberikan semua kebenaran hak akses portal yang ditetapkan bagi peranan ini.
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Pilih Ahli / Pesilat <span className="text-asmm-red">*</span>
              </label>
              {candidateMembers.length === 0 ? (
                <p className="text-xs text-slate-500 py-2">
                  Semua ahli aktif sudah memegang peranan ini atau tiada calon lain.
                </p>
              ) : (
                <MemberSelectCombobox
                  members={candidateMembers}
                  selectedMemberId={quickCandidateId}
                  onSelect={(id, member) => {
                    setQuickCandidateId(id);
                    if (member?.gelanggang) {
                      setQuickCandidateGelanggang(member.gelanggang);
                    }
                  }}
                  placeholder="Cari & pilih ahli (taip nama, IC, ID atau gelanggang)..."
                  required
                />
              )}
            </div>

            {quickCandidateId && (() => {
              const cand = members.find(m => m.id === quickCandidateId);
              if (!cand) return null;
              const candRoles: Role[] = (cand.roles && cand.roles.length > 0) ? cand.roles : [cand.role || 'Ahli'];
              return (
                <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-200 text-xs text-slate-700 space-y-1">
                  <div className="text-[10px] uppercase font-bold text-slate-500">Peranan Sedia Ada:</div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {candRoles.map(r => (
                      <span key={r} className="px-2 py-0.5 rounded-md bg-white border border-slate-200 text-[10px] font-bold text-slate-800">
                        {r}
                      </span>
                    ))}
                    <span className="text-emerald-700 font-extrabold text-[10px] bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                      + Tambah: {selectedRoleForInspector}
                    </span>
                  </div>
                </div>
              );
            })()}

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Pusat Gelanggang Latihan
              </label>
              <select
                value={quickCandidateGelanggang}
                onChange={(e) => setQuickCandidateGelanggang(e.target.value as Gelanggang)}
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-bold"
              >
                <option value="Pusaka Warisan">Pusaka Warisan</option>
                <option value="Wadi AnNour">Wadi AnNour</option>
                <option value="Paya Besar">Paya Besar</option>
                <option value="Semua Gelanggang">Semua Gelanggang (Ibu Pejabat PSSASMM)</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Catatan / Rujukan Lantikan (Pilihan)
              </label>
              <input
                type="text"
                value={quickCandidateNotes}
                onChange={(e) => setQuickCandidateNotes(e.target.value)}
                placeholder="Contoh: Lantikan Mesyuarat Pentadbiran 2026"
                className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
              />
            </div>

            <div className="flex justify-end gap-2.5 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setIsQuickAssignModalOpen(false)}
                disabled={isSubmittingQuickAssign}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 bg-slate-100 rounded-xl cursor-pointer border-none"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={isSubmittingQuickAssign || candidateMembers.length === 0}
                className="px-5 py-2 bg-asmm-red hover:bg-asmm-crimson text-white font-black text-xs rounded-xl shadow-sm transition-all flex items-center gap-1.5 cursor-pointer border-none disabled:opacity-50 active:scale-95"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>{isSubmittingQuickAssign ? 'Menyimpan...' : 'Sahkan Lantikan'}</span>
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* MODAL: Confirm Delete Role */}
      {confirmDeleteRole && (
        <Modal isOpen={true} onClose={() => setConfirmDeleteRole(null)} title="Padam Peranan?">
          <div className="space-y-4">
            <p className="text-xs text-slate-700">
              Adakah anda pasti ingin memadam peranan <strong>"{confirmDeleteRole.name}"</strong>?
              Tindakan ini tidak boleh dibatalkan.
            </p>
            <div className="flex justify-end gap-2.5 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setConfirmDeleteRole(null)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 bg-slate-100 rounded-xl cursor-pointer border-none"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={executeDeleteRole}
                className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white font-black text-xs rounded-xl shadow-sm transition-all flex items-center gap-1.5 cursor-pointer border-none active:scale-95"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Ya, Padam</span>
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* MODAL: Confirm Delete Permission */}
      {confirmDeletePerm && (
        <Modal isOpen={true} onClose={() => setConfirmDeletePerm(null)} title="Padam Hak Akses?">
          <div className="space-y-4">
            <p className="text-xs text-slate-700">
              Padam hak akses <strong>"{confirmDeletePerm.name}"</strong>?
              Hak ini akan dikeluarkan daripada semua peranan.
            </p>
            <div className="flex justify-end gap-2.5 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setConfirmDeletePerm(null)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 bg-slate-100 rounded-xl cursor-pointer border-none"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={executeDeletePerm}
                className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white font-black text-xs rounded-xl shadow-sm transition-all flex items-center gap-1.5 cursor-pointer border-none active:scale-95"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Ya, Padam</span>
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* MODAL: Confirm Reset All */}
      {confirmResetAll && (
        <Modal isOpen={true} onClose={() => setConfirmResetAll(false)} title="Set Semula Tetapan Asal?">
          <div className="space-y-4">
            <p className="text-xs text-slate-700">
              Set semula <strong>semua hak akses</strong> ke tetapan asal ASMM?
              Pindaan tersuai anda akan diset semula sepenuhnya.
            </p>
            <div className="flex justify-end gap-2.5 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setConfirmResetAll(false)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 bg-slate-100 rounded-xl cursor-pointer border-none"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={executeResetAll}
                className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white font-black text-xs rounded-xl shadow-sm transition-all flex items-center gap-1.5 cursor-pointer border-none active:scale-95"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Ya, Set Semula</span>
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
